import {
    AfterContentChecked,
    ChangeDetectorRef,
    Component,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { OrganizationsService } from 'src/app/modules/organizations/services/organizations.service';
import { MdrDashboardsService } from '../../../services/mdr-dashboards.service';

import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexFill,
    ApexGrid,
    ApexLegend,
    ApexMarkers,
    ApexPlotOptions,
    ApexStates,
    ApexStroke,
    ApexTooltip,
    ChartComponent,
} from 'ng-apexcharts';
import { MdrDashboardsSeriesDataInterface } from '../../../interfaces/mdr-dashboards-series-data.interface';
import { DashboardTable } from '../../../interfaces/mdr-dashboards-table-data.interface';
import { PieChartDataSource } from '../../../interfaces/mdr-dashboards-chart-area-data.interface';
import { DataResponse } from '../../../interfaces/mdr-dashboards.data-response.interface';
import { Observable, of } from 'rxjs';
import { TimeframeService } from '../../../../timeframe/services/timeframe.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { ThemeService } from '../../../../../services/theme.service';

export type PieChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    dataLabels: ApexDataLabels;
    colors: string[];
    legend: ApexLegend;
    fill: ApexFill;
    grid: ApexGrid;
    stroke: ApexStroke;
    markers: ApexMarkers;
    labels: string[];
    plotOptions: ApexPlotOptions;
    states: ApexStates;
    tooltip: ApexTooltip;
};

@Component({
    selector: 'app-mdr-dashboards-pie-chart',
    templateUrl: './mdr-dashboards-pie-chart.component.html',
    styleUrls: ['./mdr-dashboards-pie-chart.component.scss'],
    standalone: false,
})
export class MdrDashboardsPieChartComponent implements AfterContentChecked, OnChanges, OnInit, OnDestroy {
    @ViewChild('chart') chart: ChartComponent;
    @ViewChild('pieChartRef') pieChartRef: ChartComponent;

    /** @var startDate - start date of the chart */
    @Input() public startDate: string;

    /** @var endDate - end date of the chart */
    @Input() public endDate: string;

    /** @var series - datasource to be shown */
    @Input() public series: number[];

    /** @var colors - colors settings for each category of the series */
    @Input() public colors: string[];

    /** @var labels - labels settings for each category of the series */
    @Input() public labels: string[];

    /** @var lineCap - setting the starting and ending points of stroke; [butt, square, round] */
    @Input() public lineCap: 'butt' | 'square' | 'round' = 'round';

    /**
     * @var element - element object passed from the tab's component.
     * This object will contain specific settings and custom options for the pie chart widget
     */
    @Input() public element: PieChartDataSource;

    /** @var bulletType - set the bullet type for the legend. By default, is square */
    @Input() public bulletType = 'square';

    /** @var strokeWidth - set the stroke width for the area chart. By default, is 1 */
    @Input() public strokeWidth = this.themeService.isDarkMode ? 0 : 8;

    /** @var donutSize - set the diameter of the blank space between the pie chart widget */
    @Input() public donutSize = '75%';

    /** @var tableLegend - enable/disable the table legend style for the specific widget */
    @Input() public tableLegend = false;

    /** @var width - widget width */
    @Input() public width: number | string = 200;

    /** @var height - widget height */
    @Input() public height: number | string = 200;

    /**
     * @var stacked - enable/disable stacked option for the pie chart widget.
     * If it's true, then each category will be summed up with the previous one.
     */
    @Input() public stacked = true;

    /** @var chartType - set the chart type */
    @Input() public chartType = 'donut';

    /** @var showLegend - enable/disable legend */
    @Input() public showLegend = true;

    /** @var loading - enable/disable loading state for this specific indicator */
    @Input() public loading = false;

    /** @var placeLegendBottom - enable/disable bottom legend */
    @Input() public placeLegendBottom = false;

    /** @var legendWidthCoverage - set the legend width */
    @Input() public legendWidthCoverage = 'auto';

    @Input() public mainContainerWidth = 'auto';

    /** @var legendBottomBorder - whether the legend has bottom border or not */
    @Input() public legendBottomBorder = false;

    /** @var pieWidthCoverage - set the pie chart width */
    @Input() public pieWidthCoverage = 'auto';

    /* @var defaultEmptyValue - what to show on empty/error state */
    @Input() defaultEmptyValue = '- -';

    @Input() public applyZoom = false;
    @Input() public className = '';
    @Input() public marginBottomTitle = '20';
    @Input() takeDataFromService: boolean;
    public pieChartOptions: Partial<PieChartOptions> | any;
    public emptyPieChartOptions: Partial<PieChartOptions> | any;

    public legendDisplayedColumns: string[] = ['name', 'value'];
    public emptyDataSource_Legend1: DashboardTable[] = [];

    public areaSeries: MdrDashboardsSeriesDataInterface[];
    public areaColors: string[];
    public initialSeries: number[];
    public realData: DataResponse;
    public formattedDataForNullValues: any[] = [];
    public loadedWidget = false;
    public loadedData: Observable<DataResponse>;
    public waitingForResponse: boolean = false;
    public timeframeSub: Subscription;
    public orgSub: Subscription;

    public constructor(
        public organizationService: OrganizationsService,
        public mdrDashboardsService: MdrDashboardsService,
        public cdr: ChangeDetectorRef,
        public zone: NgZone,
        public timeframeService: TimeframeService,
        public themeService: ThemeService,
    ) {}

    ngOnInit(): void {
        this.legendWidthCoverage = this.placeLegendBottom ? '100%' : 'auto';
        this.timeframeSub = this.timeframeService.selectedTimeframe$().subscribe((val) => {
            this.waitingForResponse = true;
        });
        this.orgSub = this.organizationService.selectedOrganization$().subscribe((val) => {
            this.waitingForResponse = true;
        });
        if (this.takeDataFromService) {
            this.waitingForResponse = true;
            this.mdrDashboardsService.savedResponseSubject.asObservable().subscribe(async (val) => {
                if (val.name === this.element.url) {
                    this.waitingForResponse = true;
                    this.loadedData = of(this.mdrDashboardsService.savedResponses.find((x) => x.name === this.element.url)?.data);
                    this.loadedWidget = false;
                    this.setWidget(this.mdrDashboardsService.savedResponses.find((x) => x.name === this.element.url)?.data, null);
                    this.waitingForResponse = false;
                }
            });
        }
    }

    public ngAfterContentChecked() {
        this.cdr.detectChanges();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.element && changes.element.currentValue && changes.element.currentValue !== changes.element.previousValue) {
            this.loadedWidget = false;
            this.element = changes.element.currentValue;
            this.cdr.detectChanges();
        }
    }

    @HostListener('window:resize', ['$event'])
    sizeChange(event: Event) {
        /*
			 'quick' fix for the problem where all the charts went missing after browser resizing
			 **/
        // @ts-ignore
        let chartRef = new ApexCharts(document.querySelector('#pieChartRef'), this.pieChartOptions);
        // @ts-ignore
        let emptyChartRef = new ApexCharts(document.querySelector('#pieChartRef'), this.emptyPieChartOptions);
    }

    public setWidget(widget: any, widgetError: any) {
        let nullValuesInData = [];
        let allValuesAreNull = true;
        let showZeroValue = false;
        if (widgetError && !this.loadedWidget) {
            this.emptyDataSource_Legend1 = this.calculateLegendTotal(this.element.noDataSeries, [], this.element.noDataLabels, true);
            this.formattedDataForNullValues = this.element.noDataSeries;
            this.emptyPieChartOptions = {
                plotOptions: {
                    pie: {
                        donut: {
                            size: this.donutSize,
                            labels: {
                                show: true,
                                name: {
                                    show: false,
                                },
                                total: {
                                    show: true,
                                    showAlways: true,
                                    color: this.themeService.isDarkMode ? '#FFFFFF40' : '#373d3f',
                                    fontSize: '24px',
                                    fontFamily: 'Montserrat, sans-serif',
                                    formatter: (w: any) => {
                                        return this.defaultEmptyValue;
                                    },
                                },
                            },
                        },
                        expandOnClick: false,
                        customScale: 1,
                    },
                },
                tooltip: {
                    enabled: false,
                },
                colors: this.themeService.isDarkMode ? ['#FFFFFF40'] : ['#0000001F'],
                chart: {
                    type: this.chartType,
                    width: this.width,
                    height: this.height,
                },
                states: {
                    hover: {
                        filter: {
                            type: 'none',
                        },
                    },
                    active: {
                        filter: {
                            type: 'none',
                        },
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    show: !this.themeService.isDarkMode,
                    width: this.strokeWidth,
                },
                responsive: [],
                legend: {
                    show: false,
                },
            };
            this.loadedWidget = true;
            this.cdr.detectChanges();
        }
        if (widget && !this.loadedWidget) {
            this.realData = widget;
            this.formattedDataForNullValues = this.formatToReplaceNullValues(
                !this.takeDataFromService ? this.realData?.data?.series : this.realData?.data?.endpoint_categories.series,
            );
            nullValuesInData = !this.takeDataFromService
                ? this.realData?.data?.series?.filter((x: any) => x == null && x !== 0)
                : this.realData?.data?.endpoint_categories.series?.filter((x: any) => x == null && x !== 0);
            allValuesAreNull =
                nullValuesInData?.length ===
                (!this.takeDataFromService ? this.realData?.data?.series?.length : this.realData?.data?.endpoint_categories.series?.length);
            showZeroValue = !this.takeDataFromService
                ? this.realData?.data?.series?.length > 0 && this.calculateSeriesTotal(this.realData.data?.series) === 0
                : this.realData?.data?.endpoint_categories.series?.length > 0 &&
                  this.calculateSeriesTotal(this.realData.data?.endpoint_categories.series) === 0;
            this.pieChartOptions = {
                plotOptions: {
                    pie: {
                        donut: {
                            size: this.donutSize,
                            labels: {
                                show: true,
                                name: {
                                    show: false,
                                },
                                value: {
                                    show: true,
                                    formatter: (w: any) => {
                                        /* calculate sum of the categories to show in the middle of the chart */
                                        return !this.takeDataFromService
                                            ? this.mdrDashboardsService.intToString(
                                                  this.realData.data?.series.reduce((a: number, b: number) => {
                                                      return a + b;
                                                  }, 0),
                                                  1000,
                                                  this.element.approximate,
                                              )
                                            : this.mdrDashboardsService.intToString(
                                                  this.realData.data?.endpoint_categories.series.reduce((a: number, b: number) => {
                                                      return a + b;
                                                  }, 0),
                                                  1000,
                                                  this.element.approximate,
                                              );
                                    },
                                },
                                total: {
                                    show: true,
                                    color: this.themeService.isDarkMode ? '#FFFFFF40' : '#373d3f',
                                    fontSize: '24px',
                                    fontFamily: 'Montserrat, sans-serif',
                                    formatter: (w: any) => {
                                        /* calculate sum of the categories to show in the middle of the chart */
                                        const total = !this.takeDataFromService
                                            ? this.realData?.data?.series?.reduce((a: number, b: number) => {
                                                  return a + b;
                                              }, 0)
                                            : this.realData?.data?.endpoint_categories.series?.reduce((a: number, b: number) => {
                                                  return a + b;
                                              }, 0);
                                        return this.mdrDashboardsService.intToString(total, 1000, this.element.approximate);
                                    },
                                },
                            },
                        },
                        expandOnClick: false,
                        customScale: 1,
                    },
                },
                colors: this.colors,
                chart: {
                    style: {
                        fontFamily: 'Montserrat, sans-serif',
                    },
                    redrawOnWindowResize: true,
                    animations: {
                        enabled: false,
                    },
                    type: this.chartType,
                    width: this.width,
                    height: this.height,
                },
                tooltip: {
                    enabled: false,
                },
                states: {
                    active: {
                        filter: {
                            type: 'none',
                        },
                    },
                    hover: {
                        filter: {
                            type: 'none',
                        },
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    show: !this.themeService.isDarkMode,
                    width: this.strokeWidth,
                    lineCap: this.lineCap,
                },
                responsive: [],
                legend: {
                    show: false,
                },
            };

            if (allValuesAreNull || this.realData?.data?.length === 0) {
                this.emptyDataSource_Legend1 = this.calculateLegendTotal(this.element.noDataSeries, [], this.element.noDataLabels, true);
            } else {
                if (nullValuesInData.length === 0) {
                    this.emptyDataSource_Legend1 = this.calculateLegendTotal(
                        showZeroValue ? this.formattedDataForNullValues : this.element.noDataSeries,
                        [],
                        this.element.noDataLabels,
                        !showZeroValue,
                    );
                } else {
                    this.emptyDataSource_Legend1 = !this.takeDataFromService
                        ? this.calculateLegendTotal(
                              showZeroValue ? this.realData?.data?.series : this.element.noDataSeries,
                              [],
                              this.element.noDataLabels,
                              !showZeroValue,
                          )
                        : this.calculateLegendTotal(
                              showZeroValue ? this.realData?.data?.endpoint_categories.series : this.element.noDataSeries,
                              [],
                              this.element.noDataLabels,
                              !showZeroValue,
                          );
                }
            }

            this.formattedDataForNullValues = this.visiblePercentage(this.formattedDataForNullValues);
            this.emptyPieChartOptions = {
                plotOptions: {
                    pie: {
                        donut: {
                            size: this.donutSize,
                            labels: {
                                show: true,
                                name: {
                                    show: false,
                                },
                                total: {
                                    show: true,
                                    showAlways: true,
                                    color: this.themeService.isDarkMode ? '#FFFFFF40' : '#373d3f',
                                    fontSize: '24px',
                                    fontFamily: 'Montserrat, sans-serif',
                                    formatter: (w: any) => {
                                        if (allValuesAreNull) {
                                            return this.defaultEmptyValue;
                                        }

                                        if (showZeroValue) {
                                            return 0;
                                        }
                                        return this.defaultEmptyValue;
                                    },
                                },
                            },
                        },
                        expandOnClick: false,
                        customScale: 1,
                    },
                },
                tooltip: {
                    enabled: false,
                },
                colors: this.themeService.isDarkMode ? ['#FFFFFF40'] : ['#0000001F'],
                chart: {
                    type: this.chartType,
                    width: this.width,
                    height: this.height,
                },
                states: {
                    hover: {
                        filter: {
                            type: 'none',
                        },
                    },
                    active: {
                        filter: {
                            type: 'none',
                        },
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    show: !this.themeService.isDarkMode,
                    width: this.strokeWidth,
                },
                responsive: [],
                legend: {
                    show: false,
                },
            };
            this.loadedWidget = true;
            this.waitingForResponse = false;
            this.cdr.detectChanges();
        }
    }

    public calculateSeriesTotal(series: number[]): number {
        let total = 0;
        this.initialSeries = series;
        for (const value of series) {
            total += value ? value : 0;
        }

        return total;
    }

    public formatToReplaceNullValues(series: any[]) {
        const formatArray: any[] = [];
        series?.forEach((x) => {
            if (x == null) {
                formatArray.push(0);
            } else {
                formatArray.push(x);
            }
        });
        return formatArray;
    }

    /**
     * Function the approximate very small values to visible values
     * @param series - data series to be processed
     */
    public visiblePercentage(series: number[]): number[] {
        if (this.initialSeries === series) {
            return series;
        }
        let total = 0;
        this.initialSeries = series;
        let data: number[] = [];

        for (const value of series) {
            total += value ? value : 0;
            data.push(value);
        }

        const min_percentage = 0.03; // 3% of the total
        const min_value = min_percentage * total;

        for (const [index, value] of data.entries()) {
            if (value > 0 && value < min_value) {
                data.splice(index, 1, min_value);
            }
        }
        return data;
    }

    /**
     * This function iterates through all the categories from the series and calculate the sum for the legend
     * @param series - data series to be processed
     * @param colors - colors settings for each category
     * @param labels - labels settings for each category
     * @param enableEmtpyValue - can display empty values or not
     */
    public calculateLegendTotal(series: number[], colors: string[], labels: string[], enableEmtpyValue = false): DashboardTable[] {
        let data: DashboardTable[] = [];

        series.forEach((element: number, index: number) => {
            let temp: DashboardTable = {
                name: '',
                value: 0,
            };

            temp.name = labels[index];
            temp.value = enableEmtpyValue
                ? this.defaultEmptyValue
                : this.mdrDashboardsService.intToString(series[index], 1000, this.element.approximate);
            temp.color = enableEmtpyValue ? (this.themeService.isDarkMode ? '#FFFFFF40' : '#0000001F') : colors[index];
            data.unshift(<DashboardTable>temp);
        });

        return data;
    }

    /**
     * Function to check if the object is empty or not
     * @param obj - object to check
     */
    public isEmptyObject(obj: {} | null) {
        if (!obj) {
            return true;
        }

        return obj && Object.keys(obj).length === 0;
    }

    ngOnDestroy(): void {
        if (this.orgSub) {
            this.orgSub.unsubscribe();
        }

        if (this.timeframeSub) {
            this.timeframeSub.unsubscribe();
        }
    }
}
